/**
 * HG Filters — легкі Ajax-фільтри для Dawn
 *
 * Події що диспатчаться на document:
 *   hg:grid:updated  — грід оновлено (фільтр або сортування)
 *     event.detail.trigger — 'filter' | 'sort' | 'remove' | 'popstate'
 *     event.detail.params  — рядок search params що застосовано
 *
 * ─── Кастомний код після оновлення гріду ──────────────────────────────────
 *
 *   document.addEventListener('hg:grid:updated', function(e) {
 *     // Тут твій код — запускається після кожного Ajax-оновлення
 *     // e.detail.trigger  → 'filter' | 'sort' | 'remove' | 'popstate'
 *     // e.detail.params   → поточні search params
 *   });
 *
 *   // Тільки після сортування:
 *   document.addEventListener('hg:grid:updated', function(e) {
 *     if (e.detail.trigger !== 'sort') return;
 *     // ...
 *   });
 *
 *   // Тільки після фільтрації:
 *   document.addEventListener('hg:grid:updated', function(e) {
 *     if (e.detail.trigger !== 'filter') return;
 *     // ...
 *   });
 *
 * ──────────────────────────────────────────────────────────────────────────
 */

class HgFilters {
  constructor(form) {
    this.form = form;
    this.sectionId = form.dataset.sectionId;
    this._timer = null;

    form.addEventListener('change', (e) => {
      if (e.target.classList.contains('hg-fp__input')) return; // ціна має окремий listener
      const trigger = e.target.name === 'sort_by' ? 'sort' : 'filter';
      this._debounced(400, () => this._submit(trigger));
    });

    // Ціна — довший debounce, не тригерити при кожному символі
    form.querySelectorAll('.hg-fp__input').forEach((input) => {
      input.addEventListener('change', () => this._debounced(800, () => this._submit('filter')));
    });

    form.addEventListener('submit', (e) => e.preventDefault());
  }

  _debounced(ms, fn) {
    clearTimeout(this._timer);
    this._timer = setTimeout(fn, ms);
  }

  _submit(trigger = 'filter') {
    const params = this._serializeForm();
    this._render(window.location.pathname, params, true, trigger);
  }

  _serializeForm() {
    const data = new FormData(this.form);
    for (const [key, val] of [...data.entries()]) {
      if (val === '') data.delete(key);
    }
    return new URLSearchParams(data).toString();
  }

  _render(pathname, params, pushState, trigger) {
    const fullUrl = pathname + (params ? '?' + params : '');
    if (pushState) history.pushState({ hgParams: params }, '', fullUrl);
    const fetchUrl = fullUrl + (params ? '&' : '?') + 'section_id=' + this.sectionId;
    this._fetch(fetchUrl, trigger, params);
  }

  async _fetch(url, trigger, params) {
    const grid = document.getElementById('ProductGridContainer');
    const filtersWrap = document.getElementById('HgFiltersContainer');
    grid?.classList.add('hg-loading');

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      this._swap(grid, doc.getElementById('ProductGridContainer'));
      this._swap(filtersWrap, doc.getElementById('HgFiltersContainer'));

      HgFilters.init();
      HgFilters.bindRemoveLinks();
      HgFilters._dispatch(trigger, params);
    } catch (err) {
      console.error('[hg-filters]', err);
    } finally {
      grid?.classList.remove('hg-loading');
    }
  }

  _swap(target, source) {
    if (target && source) target.innerHTML = source.innerHTML;
  }

  // ─── Static helpers ────────────────────────────────────────────────────

  static _dispatch(trigger, params) {
    document.dispatchEvent(new CustomEvent('hg:grid:updated', {
      detail: { trigger: trigger || 'filter', params: params || '' }
    }));
  }

  static bindRemoveLinks() {
    document.querySelectorAll('[data-hg-remove]').forEach((el) => {
      if (el._hgBound) return;
      el._hgBound = true;
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const url = new URL(el.getAttribute('href'), window.location.origin);
        HgFilters._renderFromUrl(url.pathname, url.search.slice(1), 'remove');
      });
    });
  }

  static _renderFromUrl(pathname, params, trigger) {
    const fullUrl = pathname + (params ? '?' + params : '');
    history.pushState({ hgParams: params }, '', fullUrl);

    const form = document.getElementById('HgFiltersForm');
    if (!form) return;
    const fetchUrl = fullUrl + (params ? '&' : '?') + 'section_id=' + form.dataset.sectionId;
    const grid = document.getElementById('ProductGridContainer');
    grid?.classList.add('hg-loading');

    fetch(fetchUrl)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = document.getElementById('ProductGridContainer');
        const fc = document.getElementById('HgFiltersContainer');
        if (grid && doc.getElementById('ProductGridContainer'))
          grid.innerHTML = doc.getElementById('ProductGridContainer').innerHTML;
        if (fc && doc.getElementById('HgFiltersContainer'))
          fc.innerHTML = doc.getElementById('HgFiltersContainer').innerHTML;
        HgFilters.init();
        HgFilters.bindRemoveLinks();
        HgFilters._dispatch(trigger, params);
      })
      .catch((e) => console.error('[hg-filters]', e))
      .finally(() => document.getElementById('ProductGridContainer')?.classList.remove('hg-loading'));
  }

  static init() {
    document.querySelectorAll('[data-hg-filters-form]').forEach((form) => {
      if (!form._hgInstance) form._hgInstance = new HgFilters(form);
    });
  }
}

// ── popstate (back/forward браузера) ──────────────────────────────────────
window.addEventListener('popstate', (e) => {
  const params = e.state?.hgParams ?? new URLSearchParams(window.location.search).toString();
  HgFilters._renderFromUrl(window.location.pathname, params, 'popstate');
});

document.addEventListener('DOMContentLoaded', () => {
  HgFilters.init();
  HgFilters.bindRemoveLinks();
});

// ── Хуки — підключай тут або в окремому файлі ────────────────────────────
//
document.addEventListener('hg:grid:updated', function(e) {
  console.log('Grid updated by:', e.detail.trigger, e.detail.params);
});

// Реініціалізація scroll-анімації (.animatable → .animated) після Ajax-оновлення гріду
document.addEventListener('hg:grid:updated', function(e) {
  if (typeof doAnimations === 'function') {
    $(window).off('scroll', doAnimations);
    $(window).on('scroll', doAnimations);
    $(window).trigger('scroll');
  }
});